import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import DistributeurValidationModal from '../../components/validation/DistributeurValidationModal';

/** Décharges en attente de signature pour le distributeur connecté. */
const DistributeurDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('validate');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.distributeur.getMine();
      setRequests(res.data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Décharges à signer</h1>
        <p className="text-sm text-gray-500 mt-1">
          Ouvrez la demande pour consulter la décharge, puis signez-la ou refusez-la.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organisation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {requests.length > 0 ? (
                requests.map((req) => (
                  <tr key={req._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <Link to={`/requests/${req._id}`} className="text-primary-600 hover:text-primary-900">
                        {req.referenceId || req._id.slice(-6).toUpperCase()}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{req.organization || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {req.createdAt ? new Date(req.createdAt).toLocaleDateString('fr-FR') : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4">
                      <button
                        onClick={() => { setSelected(req); setMode('validate'); }}
                        className="text-green-600 hover:text-green-900"
                      >
                        Signer
                      </button>
                      <button
                        onClick={() => { setSelected(req); setMode('reject'); }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Refuser
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500">
                    Aucune décharge en attente de votre signature
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <DistributeurValidationModal
          requestId={selected._id}
          mode={mode}
          onClose={() => setSelected(null)}
          onSuccess={() => { setSelected(null); fetchRequests(); }}
        />
      )}
    </div>
  );
};

export default DistributeurDashboard;
